import { useLoaderData } from "react-router";
import { DataSourceBadge, DataSourceNotice } from "@/components/layout/data-source-notice";
import { PageHeader } from "@/components/layout/page-header";
import { Badge, Panel, PanelHeader } from "@/components/ui";
import { useDocumentTitle } from "@/lib/document-title";
import { formatDateTime } from "@/lib/format";
import type { BackendIdentity, PolicyLimit, TeamMember } from "@/lib/types";
import type { ApiFailure } from "@/services/api-client";
import { getBackendIdentity, listPolicyLimits, listTeam } from "@/services/reports";

interface AdminData {
  team: TeamMember[];
  limits: PolicyLimit[];
  identity: BackendIdentity | null;
  fallback: ApiFailure | null;
}

export async function adminLoader(): Promise<AdminData> {
  const [team, limits, { data: identity, fallback }] = await Promise.all([
    listTeam(),
    listPolicyLimits(),
    getBackendIdentity(),
  ]);
  return { team, limits, identity, fallback };
}

export function AdminPage() {
  useDocumentTitle("Admin");
  const { team, limits, identity, fallback } = useLoaderData<AdminData>();

  return (
    <>
      <PageHeader
        title="Admin"
        description="Who can act on a case, and the limits they act within."
        action={<DataSourceBadge fallback={fallback} />}
      />

      <div className="mx-auto max-w-5xl space-y-4 p-6">
        <Panel>
          <PanelHeader
            title="Your session"
            description="As the backend resolves it — the authority on what you may actually do."
          />
          {identity ? (
            <dl className="divide-y divide-line text-sm">
              <div className="flex items-center justify-between gap-4 px-4 py-2.5">
                <dt className="text-ink-muted">Email</dt>
                <dd className="text-ink">{identity.email ?? "—"}</dd>
              </div>
              <div className="flex items-center justify-between gap-4 px-4 py-2.5">
                <dt className="text-ink-muted">User id</dt>
                <dd className="numeric text-xs text-ink">{identity.id}</dd>
              </div>
              <div className="flex items-center justify-between gap-4 px-4 py-2.5">
                <dt className="text-ink-muted">Organization</dt>
                <dd className="numeric text-xs text-ink">{identity.organizationId}</dd>
              </div>
              <div className="flex items-start justify-between gap-4 px-4 py-2.5">
                <dt className="shrink-0 text-ink-muted">Permissions</dt>
                <dd className="flex flex-wrap justify-end gap-1">
                  {identity.permissions.length === 0 ? (
                    <span className="text-xs text-ink-faint">None granted</span>
                  ) : (
                    identity.permissions.map((permission) => (
                      <Badge key={permission}>{permission}</Badge>
                    ))
                  )}
                </dd>
              </div>
            </dl>
          ) : (
            <div className="px-4 py-3">
              <DataSourceNotice fallback={fallback} />
            </div>
          )}
        </Panel>

        <Panel>
          <PanelHeader title="Team" description={`${team.length} members`} />
          <ul className="divide-y divide-line">
            {team.map((member) => (
              <li
                key={member.id}
                className="flex flex-wrap items-center justify-between gap-4 px-4 py-3"
              >
                <div>
                  <p className="text-sm font-medium text-ink">{member.name}</p>
                  <p className="text-xs text-ink-muted">{member.email}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="numeric text-[0.6875rem] text-ink-faint">
                    Active {formatDateTime(member.lastActive)}
                  </span>
                  <Badge tone={member.role === "Admin" ? "accent" : "neutral"}>
                    {member.role}
                  </Badge>
                </div>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel>
          <PanelHeader
            title="Credit policy"
            description="These thresholds drive the covenant headroom and model flags shown on a case."
          />
          <ul className="divide-y divide-line">
            {limits.map((limit) => (
              <li
                key={limit.id}
                className="flex flex-wrap items-start justify-between gap-4 px-4 py-3"
              >
                <div className="max-w-xl">
                  <p className="text-sm text-ink">{limit.label}</p>
                  <p className="mt-0.5 text-xs text-ink-muted">{limit.note}</p>
                </div>
                <span className="numeric text-sm font-medium text-ink">{limit.value}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </>
  );
}
